import { Box, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import CaptionedLinearLoader from 'pages/calculator/MediaPlanning/components/Touchpoints/CaptionedLinearLoader/CaptionedLinearLoader';
import SlidersError from 'pages/calculator/MediaPlanning/components/Touchpoints/SlidersError/SlidersError';
import { useQuery } from 'react-query';
import TargetGroupSelectors from 'pages/calculator/MediaPlanning/components/Touchpoints/TikTokTouchpoint/TargetGroupSelectors/TargetGroupSelectors';
import PartnersSelectors from 'pages/calculator/MediaPlanning/components/Touchpoints/TikTokTouchpoint/PartnersSelectors/PartnersSelectors';
import { DIGITAL_FAIR_VALUE_KEY } from 'api/services/digitalFairValue';
import { getPartners } from 'api/services/mediaPlanning/getPartners';
import { useEffect } from 'react';
import usePlanStore from '../../../../../../store/planStore';
import useTiktokConfig from '../hooks/useTiktokConfig';

function TikTokTouchpoint() {
  const { t } = useTranslation();
  const {
    selectedCountry,
    countryForms,
    updateTikTokConfig,
    selectedCountryISOCode,
    generalInfoState,
  } = usePlanStore();

  const tiktokConfig = countryForms[selectedCountry!]?.tiktokConfig;

  const {
    fetchTiktokConfig,
    data: fieldConfig,
    isFetching: isFieldConfigFetching,
  } = useTiktokConfig();

  useEffect(() => {
    if (!countryForms[selectedCountry!].tiktokConfig?.targetGroup) {
      fetchTiktokConfig(undefined);
    }
  }, [countryForms[selectedCountry!].tiktokConfig?.targetGroup]);

  const {
    data: partnersData,
    isLoading: isLoadingPartners,
    isError: isErrorPartners,
  } = useQuery(
    [
      DIGITAL_FAIR_VALUE_KEY,
      'partners-tiktok',
      selectedCountryISOCode,
      generalInfoState.division,
      generalInfoState.brand,
    ],
    () =>
      getPartners(
        selectedCountryISOCode,
        generalInfoState.division,
        generalInfoState.brand
      )
  );

  const handleChangeStateValues = (key: string, value: any) => {
    updateTikTokConfig(selectedCountry!, { [key]: value });
  };

  if (isErrorPartners) {
    return <SlidersError error={`${t('mediaPlanning.loadingError')}`} />;
  }

  if (isLoadingPartners || isFieldConfigFetching) {
    return (
      <CaptionedLinearLoader
        caption={
          <Typography textAlign="center">
            <Trans i18nKey="loaders.holdOn" />
          </Typography>
        }
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: '13px',
        pb: '20px',
        rowGap: '38px',
      }}
    >
      <TargetGroupSelectors
        targetGroupSaturation={tiktokConfig?.saturation || 0}
        handleChangeStateValues={handleChangeStateValues}
        minMaxSelectorsValues={tiktokConfig}
        fetchTiktokConfig={fetchTiktokConfig}
      />
      <PartnersSelectors
        partners={tiktokConfig?.partners || []}
        partnerBudgetShare={tiktokConfig?.partnerBudgetShare || 0}
        fetchedPartnersData={{
          partners: partnersData?.data || [],
        }}
        handleChangeStateValues={handleChangeStateValues}
      />
    </Box>
  );
}

export default TikTokTouchpoint;
